import { useState, useEffect, useMemo } from 'react';
import { getStartingMonth } from "../../CarbonFootPrinting/utils/PeriodCalculationUtils";
import { buildPeriodOptions } from "../utils/dashboardHelpers";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Owns every filter-bar concern: the selected periods/locations/modules, the user-chosen
 * `aggregation` (defaulting to the company `frequency`), the derived location/period option
 * lists, the "auto-select all" effects, and the fromDate/toDate expansion for selected periods.
 *
 * @param {{ financialYear: object[], financialYearId: number|string, locations: object[], moduleOptions: object[], frequency: string }} params
 */
export const useDashboardFilters = ({ financialYear, financialYearId, locations, moduleOptions, frequency }) => {
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [selectedPeriodsValue, setSelectedPeriodsValue] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  /**
   * `aggregation` is what the user has chosen in the filter (defaults to frequency).
   */
  const [aggregation, setAggregation] = useState('MONTHLY');

  /**
   * When the company frequency is fetched from the server, sync aggregation to it
   * (so the first load uses the company's default aggregation).
   */
  useEffect(() => {
    if (frequency) {
      setAggregation(prev => prev === 'MONTHLY' || prev === frequency ? frequency : prev);
    }
  }, [frequency]);

  const locationOptions = useMemo(() =>
    locations.map(loc => ({
      value: loc.id,
      label: loc?.unitCode || `${loc?.location?.area || ""}, ${loc?.location?.city || ""}`.trim()
    })),
    [locations]
  );

  /**
   * Period options derived from the user-selected aggregation and the company starting month.
   * Labels are human-readable: "Q1 (Jul – Sep)", "H1 (Jul – Dec)", "Jul", etc.
   */
  const periodOptions = useMemo(() => {
    const startingMonth = getStartingMonth();
    return buildPeriodOptions(aggregation, startingMonth);
  }, [aggregation]);

  // Auto-select all locations when locationOptions are loaded/changed
  useEffect(() => {
    if (locationOptions.length > 0 && selectedLocations.length === 0) {
      setSelectedLocations(locationOptions.map(opt => opt.value));
    }
  }, [locationOptions]);

  // Auto-select all modules when moduleOptions are loaded/changed
  useEffect(() => {
    if (moduleOptions.length > 0 && selectedModules.length === 0) {
      setSelectedModules(moduleOptions.map(opt => opt.value));
    }
  }, [moduleOptions]);

  // Auto-select all periods when periodOptions are loaded/changed or aggregation changes
  useEffect(() => {
    if (periodOptions.length > 0) {
      setSelectedPeriods(periodOptions.map(opt => opt.value));
    }
  }, [periodOptions]);

  const getFinancialYearById = (id) => {
    const result = financialYear.find(item => item.id == id);
    return result ? result.financial_year_value : null;
  };

  const calculateDateRange = (type, period, startingMonth, year) => {
    const startMonth = ((startingMonth - 1 + (period - 1) * type) % 12) + 1;
    const startYear =
      year + Math.floor((startingMonth - 1 + (period - 1) * type) / 12);
    const endMonth = ((startMonth - 1 + type) % 12) + 1;
    const endYear = startYear + Math.floor((startMonth - 1 + type) / 12);

    const formatDate = (month, year) =>
      `${year}-${month < 10 ? `0${month}` : month}`;
    const showLevel = periodOptions.find(q => q.value === period)?.label;
    return {
      fromDate: formatDate(startMonth, startYear),
      toDate: formatDate(endMonth, endYear),
      showLevel: showLevel
    };
  };

  /**
   * Recalculate fromDate/toDate for each selected period whenever:
   * - selected periods change,
   * - financial year changes,
   * - or aggregation type changes.
   *
   * Uses `aggregation` (user-chosen) instead of the company-level `frequency`
   * so all charts reflect the same grouping the user selected.
   */
  useEffect(() => {
    if (!financialYearId || !selectedPeriods?.length) return;
    const selectedYear = getFinancialYearById(financialYearId);
    if (!selectedYear) return;
    const year = parseInt(selectedYear.split("-")[0]);
    const start = getStartingMonth();

    const dateRanges = selectedPeriods.map(period => {
      let dateRange;

      if (aggregation === "HALF_YEARLY") {
        dateRange = calculateDateRange(6, period, start, year);
      } else if (aggregation === "QUARTERLY") {
        dateRange = calculateDateRange(3, period, start, year);
      } else if (aggregation === "MONTHLY") {
        const startIndex = start - 1;
        const firstMonthAbsoluteIndex = start + period - 2;
        const firstMonthIndex =
          ((firstMonthAbsoluteIndex - startIndex + months.length) % months.length) + 1;
        dateRange = calculateDateRange(1, firstMonthIndex, start, year);
        const showLevel = periodOptions.find(q => q.value === period)?.label;
        dateRange.showLevel = showLevel;
      } else if (aggregation === "YEARLY") {
        dateRange = calculateDateRange(12, 1, start, year);
      }
      return dateRange;
    });

    setSelectedPeriodsValue(dateRanges);
  }, [selectedPeriods, financialYearId, aggregation]);

  const handleClearFilters = () => {
    // Re-select all periods, locations and modules (restores default "all selected" state)
    setSelectedPeriods(periodOptions.map(opt => opt.value));
    setSelectedLocations(locationOptions.map(opt => opt.value));
    setSelectedModules(moduleOptions.map(opt => opt.value));
  };

  return {
    selectedPeriods,
    setSelectedPeriods,
    selectedPeriodsValue,
    selectedLocations,
    setSelectedLocations,
    selectedModules,
    setSelectedModules,
    aggregation,
    setAggregation,
    locationOptions,
    periodOptions,
    handleClearFilters,
  };
};
